import React from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '~common/components/button/index.tsx'
import { TextField } from '~common/components/text-field/index.tsx'
import { Icon } from '~common/components/icon/icon.tsx'
import { ExpandableSection } from '../section-wrapper'
import {
    FormGroup,
    ErrorMessage,
    ConfirmDeleteSection,
    ConfirmText,
    ConfirmButtons,
} from '../shared-styles.tsx'
import type { SettingsModalLogic } from '../../logic'
import styled from 'styled-components'

interface DangerZoneSectionProps {
    logic: SettingsModalLogic
    state: {
        dangerZoneExpanded: boolean
        showDeleteConfirm: boolean
        deleteConfirmationText: string
        isDeleting: boolean
        deleteError: string | null
    }
    expanded: boolean
    onToggle: () => void
    showTitleInSection?: boolean
}

export const DangerZoneSection: React.FC<DangerZoneSectionProps> = ({
    logic,
    state,
    expanded,
    onToggle,
    showTitleInSection = true,
}) => {
    const { t } = useTranslation('settings')
    return (
        <ExpandableSection
            title={t('sections.dangerZone.title')}
            description={t('sections.dangerZone.description')}
            expanded={expanded}
            onToggle={onToggle}
            iconName="warning"
            iconColor={({ theme }) => theme.colors.error}
            showTitleInSection={showTitleInSection}
        >
            <DeleteAccountButton
                onClick={(e) => {
                    e.stopPropagation()
                    logic.toggleDeleteConfirm()
                }}
                size="medium"
                disabled={state.isDeleting}
                loading={state.isDeleting}
                label={t('sections.dangerZone.deleteAccount')}
                status="warning"
                variant="secondary"
            />
            {state.showDeleteConfirm && (
                <ConfirmDeleteSection>
                    <ConfirmText>
                        {t('sections.dangerZone.confirmTitle')}
                    </ConfirmText>
                    <FormGroup>
                        <TextField
                            label={t('sections.dangerZone.confirmLabel')}
                            initValue={state.deleteConfirmationText}
                            onChange={logic.updateDeleteConfirmationText}
                            placeholder={t(
                                'sections.dangerZone.confirmPlaceholder',
                            )}
                            editable={true}
                            width="100%"
                            size="large"
                        />
                    </FormGroup>
                    {state.deleteError && (
                        <ErrorMessage>{state.deleteError}</ErrorMessage>
                    )}
                    <ConfirmButtons>
                        <Button
                            label={t('cancel')}
                            onClick={(e) => {
                                e.stopPropagation()
                                logic.toggleDeleteConfirm()
                            }}
                            variant="ghost"
                            size="large"
                        />
                        <Button
                            label={t('sections.dangerZone.deleteAccountButton')}
                            onClick={(e) => {
                                e.stopPropagation()
                                logic.deleteAccount()
                            }}
                            disabled={
                                state.isDeleting ||
                                state.deleteConfirmationText.trim() !== 'Delete'
                            }
                            loading={state.isDeleting}
                            size="large"
                        />
                    </ConfirmButtons>
                </ConfirmDeleteSection>
            )}
        </ExpandableSection>
    )
}

const DeleteAccountButton = styled(Button)`
    width: 100%;
    justify-content: center;
`
